@use '@/styles/variables' as *;

// ─── Wrapper ──────────────────────────────────────────────────────────────────

.journey {
  width: 100%;
  max-width: 680px;
  margin: 0 auto;
}

// ─── Timeline ─────────────────────────────────────────────────────────────────

.timeline {
  list-style: none;
  padding: 0;
  margin: 0;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 0.4375rem;
    top: 0.75rem;
    bottom: 0.75rem;
    width: 1px;
    background: linear-gradient(to bottom, transparent, var(--border) 6%, var(--border) 94%, transparent);
  }
}

// ─── Item ─────────────────────────────────────────────────────────────────────

.item {
  opacity: 0;
  transform: translateX(18px);
  transition:
    opacity 0.5s ease,
    transform 0.5s ease;

  &.visible {
    opacity: 1;
    transform: translateX(0);
  }
}

// ─── Trigger ──────────────────────────────────────────────────────────────────

.trigger {
  width: 100%;
  background: none;
  border: none;
  padding: 2rem 0;
  display: flex;
  gap: 1.75rem;
  align-items: flex-start;
  text-align: left;
  cursor: pointer;
  color: inherit;
  font: inherit;

  &:hover .dot {
    transform: scale(1.3);
    box-shadow: 0 0 0 5px color-mix(in srgb, var(--forest) 18%, transparent);
    background: color-mix(in srgb, var(--forest) 80%, var(--foreground));
  }

  &:focus-visible {
    outline: 2px solid var(--foreground);
    outline-offset: 3px;
    border-radius: var(--radius);
  }
}

// ─── Rail & dot ───────────────────────────────────────────────────────────────

.rail {
  flex-shrink: 0;
  width: 0.875rem;
  padding-top: 0.15rem;
}

.dot {
  display: block;
  width: 0.875rem;
  height: 0.875rem;
  border-radius: 50%;
  background: var(--forest);
  transition:
    transform 0.25s ease,
    box-shadow 0.25s ease,
    background 0.25s ease;
}

.current .dot {
  animation: pulse 2.6s ease-in-out infinite;
}

@keyframes pulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 color-mix(in srgb, var(--forest) 55%, transparent);
  }

  55% {
    box-shadow: 0 0 0 7px color-mix(in srgb, var(--forest) 0%, transparent);
  }
}

// ─── Content ──────────────────────────────────────────────────────────────────

.content {
  flex: 1;
  min-width: 0;
}

.year {
  display: block;
  font-size: 0.7rem;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--muted-foreground);
  margin-bottom: 0.35rem;
}

.title {
  font-size: clamp(1.1rem, 2vw, 1.35rem);
  font-weight: 600;
  font-family: var(--font-display);
  letter-spacing: -0.02em;
  color: var(--foreground);
  line-height: 1.3;
  margin-bottom: 0.6rem;
  transition: color 0.25s ease;
}

.active .title {
  color: var(--forest);
}

.body {
  font-size: 0.9rem;
  color: var(--muted-foreground);
  line-height: 1.7;
}

// ─── Tags (expandable) ────────────────────────────────────────────────────────

.tagsWrapper {
  display: grid;
  grid-template-rows: 0fr;
  margin-top: 0;
  transition:
    grid-template-rows 0.35s ease,
    margin-top 0.35s ease;
}

.active .tagsWrapper {
  grid-template-rows: 1fr;
  margin-top: 0.85rem;
}

.tagsInner {
  overflow: hidden;
}

.tags {
  list-style: none;
  padding: 0 0 0.25rem;
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.tag {
  font-size: 0.75rem;
  padding: 0.2rem 0.65rem;
  border-radius: 999px;
  background: color-mix(in srgb, var(--forest) 10%, var(--background));
  color: var(--forest);
  font-weight: 500;
  letter-spacing: 0.02em;
  border: 1px solid color-mix(in srgb, var(--forest) 22%, transparent);
}

// ─── Responsive ───────────────────────────────────────────────────────────────

@media (max-width: #{$breakpoint-sm}) {
  .trigger {
    gap: 1.25rem;
    padding: 1.5rem 0;
  }
}

// ─── Reduced motion ───────────────────────────────────────────────────────────

@media (prefers-reduced-motion: reduce) {
  .item {
    opacity: 1;
    transform: none;
    transition: none;
  }

  .current .dot {
    animation: none;
  }
}
